import type { StreetChatStreamInfo, StreamSlot } from "../types/stream";
import { slotEntries } from "./streamSlot";

export type CastLocalId = "VMPL_FILE_1" | "VMPL_FILE_2" | "VMPL_LINK_1";

/** One cast card in the dock. A new cast in the same slot has a new streamId, so a new key. */
export interface DockCast {
  key: string;
  slotId: number;
  gcId: number;
  localId: CastLocalId;
  own: boolean;
  /** When this tab first saw the cast (own casts: when ownCastStore started it). */
  startedAt: number;
}

/**
 * One cast's identity. The server sends streamId null for VMPL file/link slots, so then the
 * casting user tells casts apart, plus the media for a file. A link slot's media is the queue's
 * current item, which changes on every advance of a post cast, so it is not part of a link cast's
 * identity. A same-account re-cast elsewhere is preceded by STOP_STREAM (the slot must be freed
 * first), which ends the own cast, so ignoring the media cannot confuse two casts. A freed slot
 * clears its state (castDockStore), so the next cast starts fresh.
 */
export const castKeyOf = (slot: StreamSlot): string =>
  `${slot.id}:${slot.streamId || `${slot.streamingUser?.userId ?? ""}|${slot.streamContentType === "VMPL_LINK" ? "link" : (slot.mediaId ?? slot.mediaUrl ?? "")}`}`;

export const isCastLocalId = (id: string): id is CastLocalId =>
  id === "VMPL_FILE_1" || id === "VMPL_FILE_2" || id === "VMPL_LINK_1";

/** The open GC's busy file/link slots that are not this tab's own casts and not hidden. */
export function watchedCastsOf(
  info: StreetChatStreamInfo | undefined,
  gcId: number,
  ownKeys: ReadonlySet<string>,
  hidden: Readonly<Record<string, true>>,
  seen: Readonly<Record<string, number>>,
): DockCast[] {
  const out: DockCast[] = [];
  for (const { localId, slot } of slotEntries(info)) {
    if (!isCastLocalId(localId) || slot.available !== false) continue;
    const key = castKeyOf(slot);
    if (ownKeys.has(key) || hidden[key]) continue;
    out.push({ key, slotId: slot.id, gcId, localId, own: false, startedAt: seen[key] ?? Number.MAX_SAFE_INTEGER });
  }
  return out.sort((a, b) => a.startedAt - b.startedAt);
}

const byStart = (a: DockCast, b: DockCast) => a.startedAt - b.startedAt;

/** Android's dock order for casts: your own first, then watched; each in start order. */
export function dockOrder(own: DockCast[], watched: DockCast[]): DockCast[] {
  return [...[...own].sort(byStart), ...[...watched].sort(byStart)];
}

export type PipTarget = { kind: "live" } | { kind: "cast"; key: string } | null;

/** Android's `pipTargetOf`: live video > own casts > watched casts; within a group the newest. */
export function pipTargetOf(liveShowing: boolean, casts: DockCast[]): PipTarget {
  if (liveShowing) return { kind: "live" };
  const newest = (list: DockCast[]) => list.reduce<DockCast | null>((n, x) => (!n || x.startedAt > n.startedAt ? x : n), null);
  const pick = newest(casts.filter((x) => x.own)) ?? newest(casts.filter((x) => !x.own));
  return pick ? { kind: "cast", key: pick.key } : null;
}
